import { UploadOutlined } from '@ant-design/icons';
import { Project } from '@common';
import { PrimaryButton, InputMask } from '@components/atoms';
import { Button, Col, Form, Input, Modal, Row, Upload, InputNumber, DatePicker, Checkbox, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { UploadFile, RcFile } from 'antd/es/upload/interface';
import dayjs from 'dayjs';
import './styles.css';

const { TextArea } = Input;
const { Title: AntTitle } = Typography;

interface AddProjectModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onSave: (project: any) => void;
  initialValues?: Project;
}

export const AddProjectModal = ({ isOpen, onCancel, onSave, initialValues }: AddProjectModalProps) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const startDate = Form.useWatch('startDate', form);
  const endDate = Form.useWatch('endDate', form);

  const calculateDuration = () => {
    if (!startDate || !endDate) {
      return '';
    }
    
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    const months = end.diff(start, 'month', true);
    const days = end.diff(start, 'day');
    
    if (months >= 1) {
      const wholeMonths = Math.floor(months);
      const remainingDays = Math.floor((months % 1) * 30);
      if (wholeMonths > 0 && remainingDays > 0) {
        return `${wholeMonths} ${wholeMonths === 1 ? 'month' : 'months'} ${remainingDays} ${remainingDays === 1 ? 'day' : 'days'}`;
      } else if (wholeMonths > 0) {
        return `${wholeMonths} ${wholeMonths === 1 ? 'month' : 'months'}`;
      } else {
        return `${days} ${days === 1 ? 'day' : 'days'}`;
      }
    } else {
      return `${days} ${days === 1 ? 'day' : 'days'}`;
    }
  };

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
      setImageFile(null);
      setFileList([]);

      if (initialValues) {
        const formValues = {
          ...initialValues,
          name: initialValues.name || initialValues.title,
          // Converter datas para objetos dayjs
          startDate: initialValues.startDate ? dayjs(initialValues.startDate) : undefined,
          endDate: initialValues.endDate ? dayjs(initialValues.endDate) : undefined,
          // Garantir que os valores dos big numbers sejam carregados corretamente
          showDuration: initialValues.showDuration !== undefined ? initialValues.showDuration : true,
          showYear: initialValues.showYear !== undefined ? initialValues.showYear : true,
          showBudget: initialValues.showBudget !== undefined ? initialValues.showBudget : true,
          showEarthMoved: initialValues.showEarthMoved !== undefined ? initialValues.showEarthMoved : true,
        };
        form.setFieldsValue(formValues);
      }
    }
  }, [isOpen, initialValues, form]);

  const handleImageUpload = (file: UploadFile) => {
    // Verificar se é um objeto File real ou UploadFile do Ant Design
    let actualFile: File | null = null;
    
    if (file.originFileObj) {
      actualFile = file.originFileObj;
    } else if (file instanceof File) {
      actualFile = file;
    } else {
      return false;
    }
    
    // Salvar o arquivo
    if (actualFile) {
      setImageFile(actualFile);
      
      // Criar o fileList para mostrar na interface
      const newFileList: UploadFile[] = [{
        uid: file.uid || Date.now().toString(),
        name: actualFile.name,
        status: 'done',
        originFileObj: actualFile as RcFile,
      }];
      setFileList(newFileList);
    }
    
    return false; // Prevent automatic upload
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      const projectData = {
        name: values.name,
        description: values.description,
        budget: values.budget,
        siteArea: values.siteArea,
        earthMoved: values.earthMoved,
        startDate: values.startDate ? dayjs(values.startDate).format('YYYY-MM-DD') : undefined,
        endDate: values.endDate ? dayjs(values.endDate).format('YYYY-MM-DD') : undefined,
        completionYear: values.completionYear,
        image: imageFile,
        // Big numbers display configuration
        showDuration: !!values.showDuration,
        showYear: !!values.showYear,
        showBudget: !!values.showBudget,
        showEarthMoved: !!values.showEarthMoved,
      };

      onSave(projectData);
      form.resetFields();
      setImageFile(null);
      setFileList([]);

    } catch (error) {
      console.error('Error validating form:', error);
    } finally {
      setLoading(false);
    }
  };

  const modalTitle = initialValues
    ? <div className="!font-['Android_101'] !font-normal">EDIT PROJECT</div>
    : <div className="!font-['Android_101'] !font-normal">ADD NEW PROJECT</div>;

  return (
    <Modal
      title={modalTitle}
      open={isOpen}
      onCancel={onCancel}
      footer={null}
      width={700}
      className="project-modal"
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
      >
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="name"
              label={<span className="text-white font-['Barlow']">PROJECT NAME</span>}
              rules={[{ required: true, message: 'Please enter the project name' }]}
            >
              <Input className="bg-zinc-800 border-zinc-700 !text-white !font-['Barlow']" placeholder="Ex: West River Street Park" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="description"
              label={<span className="text-white font-['Barlow']">DESCRIPTION</span>}
            >
              <TextArea
                rows={4}
                className="bg-zinc-800 border-zinc-700 !text-white !font-['Barlow']"
                placeholder="Detailed project description"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="budget"
              label={<span className="text-white font-['Barlow']">BUDGET</span>}
            >
              <InputMask
                mask="dollar"
                className="bg-zinc-800 border-zinc-700 !text-white !font-['Barlow']"
                placeholder="$2,500,000"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="completionYear"
              label={<span className="text-white font-['Barlow']">COMPLETION YEAR</span>}
            >
              <InputNumber 
                min={1900}
                max={2100}
                className="w-full bg-zinc-800 border-zinc-700 !text-white !font-['Barlow']"
                placeholder="Ex: 2024"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="siteArea"
              label={<span className="text-white font-['Barlow']">SITE AREA</span>}
            >
              <Input 
                className="bg-zinc-800 border-zinc-700 !text-white !font-['Barlow']" 
                placeholder="Ex: 15 acres" 
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="earthMoved"
              label={<span className="text-white font-['Barlow']">EARTH MOVED</span>}
            >
              <Input 
                className="bg-zinc-800 border-zinc-700 !text-white !font-['Barlow']" 
                placeholder="Ex: 25,000 cubic yards" 
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="startDate"
              label={<span className="text-white font-['Barlow']">START DATE</span>}
            >
              <DatePicker 
                className="w-full bg-zinc-800 border-zinc-700 !text-white !font-['Barlow']" 
                placeholder="Select start date"
                format="MM/DD/YYYY"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="endDate"
              label={<span className="text-white font-['Barlow']">END DATE</span>}
            >
              <DatePicker 
                className="w-full bg-zinc-800 border-zinc-700 !text-white !font-['Barlow']" 
                placeholder="Select end date"
                format="MM/DD/YYYY"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={<span className="text-white font-['Barlow']">DURATION</span>}
            >
              <Input 
                className="bg-zinc-800 border-zinc-700 !text-white !font-['Barlow']" 
                placeholder="Calculated automatically"
                value={calculateDuration()}
                readOnly
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={<span className="text-white font-['Barlow']">PROJECT IMAGE</span>}
            >
              <Upload
                accept="image/*"
                beforeUpload={handleImageUpload}
                maxCount={1}
                showUploadList={true}
                fileList={fileList}
                onRemove={() => {
                  setImageFile(null);
                  setFileList([]);
                  return true;
                }}
                onChange={({ fileList: newFileList }) => {
                  setFileList(newFileList);
                  if (newFileList.length === 0) {
                    setImageFile(null);
                  }
                }}
                customRequest={({ onSuccess }) => {
                  // Simular sucesso imediatamente para que o arquivo apareça na lista
                  setTimeout(() => {
                    onSuccess?.({});
                  }, 0);
                }}
              >
                <Button 
                  className="!border-gray-700"
                  icon={<UploadOutlined className="!text-white" />}
                >
                  <span className="font-android !font-normal !text-gray-100">CHOOSE IMAGE</span>
                </Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>

        {/* Big Numbers Display Configuration */}
        <Row gutter={16}>
          <Col span={24}>
            <AntTitle level={5} className="!text-white !font-['Android_101'] !font-normal !mb-4">
              BIG NUMBERS DISPLAY CONFIGURATION
            </AntTitle>
            <p className="text-gray-300 font-['Barlow'] text-sm mb-4">
              Select which information should be displayed as big numbers in the featured project:
            </p>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={6}>
            <Form.Item
              name="showDuration"
              valuePropName="checked"
            >
              <Checkbox className="text-white font-['Barlow']">
                Project Duration
              </Checkbox>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="showYear"
              valuePropName="checked"
            >
              <Checkbox className="text-white font-['Barlow']">
                Year
              </Checkbox>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="showBudget"
              valuePropName="checked"
            >
              <Checkbox className="text-white font-['Barlow']">
                Budget
              </Checkbox>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="showEarthMoved"
              valuePropName="checked"
            >
              <Checkbox className="text-white font-['Barlow']">
                Earth Moved
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16} justify="end">
          <Col>
            <PrimaryButton
              onClick={onCancel}
              className="mr-2 !text-white !font-['Barlow']"
            >
              CANCEL
            </PrimaryButton>
            <PrimaryButton
              onClick={handleSubmit}
              loading={loading}
              className="!text-black !font-['Barlow']"
            >
              {initialValues ? 'UPDATE' : 'SAVE'}
            </PrimaryButton>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}; 